import { api } from './api';
import { Company, CreateCompanyData } from '../types/company';

class CompanyService {
    async create(data: CreateCompanyData): Promise<Company> {
        const response = await api.post<Company>('/companies', data);
        return response.data;
    }

    async get(id: string): Promise<Company> {
        const response = await api.get<Company>(`/companies/${id}`);
        return response.data;
    }

    async update(id: string, data: Partial<CreateCompanyData>): Promise<Company> {
        const response = await api.put<Company>(`/companies/${id}`, data);
        return response.data;
    }

    async getMyCompany(): Promise<Company> {
        const response = await api.get<Company>('/company/my');
        return response.data;
    }

    // Admin methods
    async adminGet(id: string): Promise<Company> {
        const response = await api.get<Company>(`/admin/companies/${id}`);
        return response.data;
    }

    async adminUpdate(id: string, data: Partial<CreateCompanyData>): Promise<Company> {
        const response = await api.put<Company>(`/admin/companies/${id}`, data);
        return response.data;
    }

    async adminDestroy(id: string): Promise<void> {
        await api.delete(`/admin/companies/${id}`);
    }

    async adminRestore(id: string): Promise<void> {
        await api.post(`/admin/companies/${id}/restore`);
    }

    async adminAddCredits(id: string, amount: number, description: string): Promise<unknown> {
        const response = await api.post(`/admin/companies/${id}/credits`, { amount, description });
        return response.data;
    }

    async adminChangeSubscription(id: string, planId: string): Promise<unknown> {
        const response = await api.put(`/admin/companies/${id}/subscription`, { plan_id: planId });
        return response.data;
    }

    async adminListUsers(id: string): Promise<unknown> {
        const response = await api.get(`/admin/companies/${id}/users`);
        return response.data;
    }

    async adminChangeUserRole(companyId: string, userId: string, role: string): Promise<unknown> {
        const response = await api.put(`/admin/companies/${companyId}/users/${userId}/role`, { role });
        return response.data;
    }

    async adminDeleteUser(companyId: string, userId: string): Promise<unknown> {
        const response = await api.delete(`/admin/companies/${companyId}/users/${userId}`);
        return response.data;
    }

    // Company admin: toggle member active/inactive
    async toggleUserActive(userId: string): Promise<unknown> {
        const response = await api.post(`/company/users/${userId}/toggle-active`);
        return response.data;
    }

    // Snapfy admin: toggle member active/inactive
    async adminToggleUserActive(companyId: string, userId: string): Promise<unknown> {
        const response = await api.post(`/admin/companies/${companyId}/users/${userId}/toggle-active`);
        return response.data;
    }

    // Snapfy admin: reset user password
    async adminResetPassword(companyId: string, userId: string): Promise<unknown> {
        const response = await api.post(`/admin/companies/${companyId}/users/${userId}/reset-password`);
        return response.data;
    }

    // Snapfy admin: create user in company
    async adminCreateUser(companyId: string, data: { name: string; email: string; role: string }): Promise<unknown> {
        const response = await api.post(`/admin/companies/${companyId}/users/create`, data);
        return response.data;
    }

    // Snapfy admin: invite user by email
    async adminInviteUser(companyId: string, email: string): Promise<unknown> {
        const response = await api.post(`/admin/companies/${companyId}/users/invite`, { email });
        return response.data;
    }

    // Snapfy admin: add existing user to company
    async adminAddExistingUser(companyId: string, userId: string, role: string): Promise<unknown> {
        const response = await api.post(`/admin/companies/${companyId}/users`, { user_id: userId, role });
        return response.data;
    }

    // Snapfy admin: search users by email
    async adminSearchUsers(email: string): Promise<{ id: string; name: string; email: string; companyId?: string }> {
        const response = await api.get(`/admin/invoices/search-user?email=${encodeURIComponent(email)}`);
        return response.data as { id: string; name: string; email: string; companyId?: string };
    }

    // Snapfy admin: approve company
    async adminApprove(id: string): Promise<unknown> {
        const response = await api.post(`/admin/companies/${id}/approve`);
        return response.data;
    }

    // Snapfy admin: reject company
    async adminReject(id: string, reason: string): Promise<unknown> {
        const response = await api.post(`/admin/companies/${id}/reject`, { reason });
        return response.data;
    }
}

export const companyService = new CompanyService();
